<template>
  <div class="am3-input-heading-container @container">
    <AM3InputLabel
      :label="label"
      :required="required"
      :input-id="inputId"
    />
    <div
      class="am3-input-heading-wrapper relative min-w-0 inline-flex w-full after:inset-x-0 after:bottom-0 after:h-[2px] after:absolute after:transition-colors after:duration-75 after:pointer-events-none"
      :class="[
        disabled ? 'disabled' : '',
        (validationState === false || typeof validationState === 'string') ? 'error' : '',
      ]"
      :data-test-id="generateDataTestId(dataTestId, 'input-heading-wrapper')"
    >
      <input
        :id="inputId"
        ref="inputRef"
        :value="modelValue"
        :placeholder="placeholder"
        :disabled="disabled"
        :required="required"
        :maxlength="maxlength"
        :data-test-id="generateDataTestId(dataTestId, 'input-heading')"
        :class="sizeClass"
        class="w-full text-primary pr-4 [&:disabled]:pointer-none focus:relative [&:disabled]:select-none leading-[44px] @sm:leading-[48px] @sm:md:leading-[64px] bg-transparent font-bold pb-1 outline-none"
        @input="updateValue"
        @change="$emit('change', $event.target.value.trim())"
        @keydown="handleKeyDown"
        @paste="handlePaste"
        @blur="handleBlur"
        @click="emit('click', $event)"
      >
    </div>

    <AM3InputError
      :error="validationState"
      :data-test-id="generateDataTestId(dataTestId, 'input-heading-error')"
    />
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { v4 as uuidv4 } from 'uuid'
import '@/components/am3-generic/core/theme.css'
import { generateDataTestId } from '@/components/am3-generic/core/dataTestId'
import { useFormValidation } from '@/components/am3-generic/inputs/useFormValidation'
import AM3InputError from '@/components/am3-generic/inputs/AM3InputError.vue'
import AM3InputLabel from '@/components/am3-generic/inputs/AM3InputLabel.vue'

interface Props {
  modelValue: string
  label?: string | false
  placeholder?: string
  disabled?: boolean
  required?: boolean
  maxlength?: number
  validate?: (value: string) => boolean | string
  validateOnBlur?: boolean
  restrict?: (event: Event) => boolean
  dataTestId?: string
  sizeClass?: string
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: '',
  label: false,
  placeholder: 'Enter heading',
  disabled: false,
  required: false,
  maxlength: undefined,
  validate: () => true,
  validateOnBlur: false,
  restrict: () => true,
  dataTestId: 'heading-input',
  sizeClass: 'text-lg @sm:text-4xl',
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void
  (e: 'change', value: string): void
  (e: 'enter', value: string): void
  (e: 'blur'): void
  (e: 'click', event: MouseEvent): void
  (e: 'escape'): void
}>()

// Generate unique ID for the input
const inputId = ref(`am3-input-heading-${uuidv4()}`)
const inputRef = ref<HTMLInputElement | null>(null)

// Setup form validation
const {
  validationState,
  runValidation,
  resetValidation,
  validateOnBlur,
  handleBlur: _handleBlur,
} = useFormValidation(props.validate, props.modelValue, props.required)

// Sync the validateOnBlur prop with the composable ref
validateOnBlur.value = props.validateOnBlur

// Handle blur with custom logic
const handleBlur = () => {
  _handleBlur(props.modelValue)
  emit('blur')
}

// Update model value
const updateValue = (event: Event) => {
  const target = event.target as HTMLInputElement
  emit('update:modelValue', target.value)
}

// Handle keydown event with restrictions
const handleKeyDown = (event: KeyboardEvent) => {
  if (!props.restrict(event)) {
    event.preventDefault()
  }

  if (event.key === 'Enter') {
    emit('enter', (event.target as HTMLInputElement).value)
  }

  if (event.key === 'Escape') {
    emit('escape')
  }
}

// Handle paste event with restrictions
const handlePaste = (event: ClipboardEvent) => {
  if (!props.restrict(event)) {
    event.preventDefault()
  }
}

// Focus the input
const focus = () => {
  inputRef.value?.focus()
}

// Expose methods for the component instance
defineExpose({
  runValidation,
  resetValidation,
  focus,
})
</script>

<style>
.am3-input-heading-wrapper::after {
  background-image: repeating-linear-gradient(
      to right,
      var(--surface-dark) 0 12px,
      transparent 14px 20px
  );
}

.am3-input-heading-wrapper:has(input:focus)::after {
  background-image: repeating-linear-gradient(
      to right,
      theme('colors.brand') 0 12px,
      transparent 14px 20px
  );
}
</style>
